import React, { useEffect, useState } from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useEditor, useValue } from 'tldraw';
import { useBoardActivities } from '../context/BoardActivitiesContext';
import { useBoardStore } from '../store/useBoardStore';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { readShapeMeaningPreview } from '../utils/shapeMeaningPreview';
import { formatAiCostLabel } from '../utils/aiCostFormat';
import { getCanvasMessages } from '../utils/canvasI18n';
import { constraintDisplayText, constraintKindLabelKey } from '../utils/contextMemory';
import { ledgerRowsRemovedWithShape } from '../utils/withdrawConstraints';
import { LEDGER_ACCENT } from '../utils/ledgerAccent';
import noFavicon from 'components/simpleContent/no_favicon.svg';

const LEDGER_HOVER_LIMIT = 4;

function actorFromShape(shape, activityById) {
  if (!shape) return null;
  const meta = shape.meta || {};
  if (meta.createdByUsername || meta.updatedByUsername) {
    return {
      username: meta.updatedByUsername || meta.createdByUsername,
      profileImageUrl: meta.updatedByProfileImageUrl || meta.createdByProfileImageUrl || '',
    };
  }
  if (shape.type === 'pin-card' && meta.activityId) {
    const activity = activityById?.(meta.activityId);
    const writer = activity?.action?.writer?.[0] || activity?.writer?.[0];
    if (writer?.username) {
      return {
        username: writer.username,
        profileImageUrl: writer.profileImageUrl || '',
      };
    }
  }
  return null;
}

function PinHoverIcon({ icon }) {
  if (!icon) return null;

  if (icon.kind === 'ai') {
    return (
      <SmartToyOutlinedIcon
        sx={{
          fontSize: 14,
          color: '#94a3b8',
          flexShrink: 0,
          mt: '1px',
        }}
      />
    );
  }

  if (icon.kind === 'favicon') {
    const src = icon.host
      ? `https://${icon.host}/favicon.ico`
      : noFavicon;
    return (
      <Box
        component="img"
        src={src}
        alt=""
        onError={e => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = noFavicon;
        }}
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          mt: '1px',
          bgcolor: 'rgba(148, 163, 184, 0.2)',
        }}
      />
    );
  }

  return null;
}

/**
 * On hover, show the intent/meaning text AI would use for the shape,
 * plus who added it (avatar + name) inside the same tooltip.
 * Pins: favicon/type icon + title above meaning text.
 * Same detail in dev and production: kind, source, extras, AI cost.
 * Blocker done: shown whenever this shape filled a coach gap.
 * Ledger: active claims this shape added. Deleting it removes those rows.
 */
export default function MeaningHoverOverlay() {
  const editor = useEditor();
  const { activityById } = useBoardActivities();
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const doneFills = useBoardStore(
    state => state.board?.contextRecommendations?.userProgress?.doneFills || null
  );
  const constraints = useBoardStore(
    state => state.board?.contextRecommendations?.constraints || null
  );
  const [screenPos, setScreenPos] = useState(null);

  const contextMenuOpen = useValue(
    'meaning-hover-context-menu',
    () => {
      const open = editor.menus?.getOpenMenus?.() || [];
      return open.some(id => String(id).startsWith('context menu'));
    },
    [editor]
  );

  const hoveredShapeId = useValue(
    'meaning-hover-shape',
    () => {
      const hovered = editor.getHoveredShapeId?.() || null;
      if (hovered) return hovered;
      const point = editor.inputs?.currentPagePoint;
      if (!point || typeof editor.getShapeAtPoint !== 'function') return null;
      const atPoint = editor.getShapeAtPoint(point, {
        hitInside: true,
        hitLabels: true,
        margin: 12,
      });
      if (!atPoint) return null;
      if (atPoint.type === 'text' || atPoint.type === 'note') return atPoint.id;
      return null;
    },
    [editor]
  );

  const meaning = useValue(
    'meaning-hover-info',
    () => {
      if (!hoveredShapeId) return null;
      const shape = editor.getShape(hoveredShapeId);
      const preview = readShapeMeaningPreview(editor, shape, activityById, { doneFills });
      if (!preview) return null;
      return preview;
    },
    [editor, hoveredShapeId, activityById, doneFills]
  );

  const actor = useValue(
    'meaning-hover-actor',
    () => {
      if (!hoveredShapeId) return null;
      return actorFromShape(editor.getShape(hoveredShapeId), activityById);
    },
    [editor, hoveredShapeId, activityById]
  );

  const ledgerRows = useValue(
    'meaning-hover-ledger',
    () => {
      if (!hoveredShapeId) return [];
      return ledgerRowsRemovedWithShape(constraints, editor.getShape(hoveredShapeId));
    },
    [editor, hoveredShapeId, constraints]
  );

  useEffect(() => {
    if (!hoveredShapeId || (!meaning && !actor && !ledgerRows.length)) {
      setScreenPos(null);
      return undefined;
    }
    const shape = editor.getShape(hoveredShapeId);
    if (!shape) {
      setScreenPos(null);
      return undefined;
    }
    const bounds = editor.getShapePageBounds(shape);
    if (!bounds) {
      setScreenPos(null);
      return undefined;
    }
    const screen = editor.pageToScreen({
      x: bounds.x + bounds.w / 2,
      y: bounds.y + bounds.h,
    });
    setScreenPos({ x: screen.x, y: screen.y + 10 });
    return undefined;
  }, [editor, hoveredShapeId, meaning, actor, ledgerRows.length]);

  if (contextMenuOpen || (!meaning && !actor && !ledgerRows.length) || !screenPos) return null;

  const showPinHeader = Boolean(meaning?.title || meaning?.icon);
  const meaningDiffersFromTitle = Boolean(
    meaning?.title
    && String(meaning.text || '').trim()
    && String(meaning.text).trim() !== String(meaning.title).trim()
  );
  const showMeaningText = Boolean(
    meaning
    && String(meaning.text || '').trim()
    && (!meaning.title || meaningDiffersFromTitle)
  );
  const isPartialFill = meaning?.blockerDone?.kind === 'partial';
  const blockerDoneLabel = meaning?.blockerDone?.doneKey
    ? `${
        meaning.blockerDone.kind === 'validation'
          ? 'Done validation'
          : isPartialFill
            ? 'Partial fill'
            : 'Done blocker'
      } · ${meaning.blockerDone.doneKey}`
    : '';
  const showExtras = (meaning?.extras || []).filter(
    extra => !String(extra.label || '').startsWith('done ')
  );
  const visibleLedgerRows = ledgerRows.slice(0, LEDGER_HOVER_LIMIT);
  const hiddenLedgerCount = Math.max(0, ledgerRows.length - visibleLedgerRows.length);
  const showLedger = visibleLedgerRows.length > 0;
  const showMeaningHeader = Boolean(meaning);
  const showActorFollows = Boolean(
    meaning && (showMeaningHeader || blockerDoneLabel || showPinHeader || showMeaningText || showExtras.length)
  );
  const contentFollowsLedger = Boolean(
    actor || showMeaningHeader || blockerDoneLabel || showPinHeader || showMeaningText || showExtras.length
  );

  return (
    <Box
      className="board-meaning-hover"
      sx={{
        position: 'fixed',
        left: screenPos.x,
        top: screenPos.y,
        transform: 'translate(-50%, 0)',
        zIndex: 1200,
        pointerEvents: 'none',
        maxWidth: 340,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'rgba(15, 23, 42, 0.94)',
        color: '#e2e8f0',
        boxShadow: 3,
        border: '1px solid rgba(148, 163, 184, 0.35)',
      }}
    >
      {showLedger ? (
        <Box
          sx={{
            mb: contentFollowsLedger ? 0.75 : 0,
            pb: contentFollowsLedger ? 0.6 : 0,
            borderBottom: contentFollowsLedger ? '1px solid rgba(148, 163, 184, 0.28)' : 'none',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: 0.2,
              color: '#fcd34d',
              mb: 0.4,
              fontSize: 10,
              lineHeight: 1.3,
            }}
          >
            {messages.pinLedgerHoverTitle}
          </Typography>
          {visibleLedgerRows.map((row) => {
            const text = constraintDisplayText(row);
            const kindLabel = messages[constraintKindLabelKey(row)] || messages.constraintKindFact;
            return (
              <Typography
                key={row.id || `${kindLabel}:${text.slice(0, 24)}`}
                variant="caption"
                sx={{
                  display: 'block',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                  color: '#f8fafc',
                  fontSize: 12,
                  '& + &': { mt: 0.35 },
                }}
              >
                <Box component="span" sx={{ color: LEDGER_ACCENT, fontWeight: 700 }}>
                  {kindLabel}
                  {' · '}
                </Box>
                {text}
              </Typography>
            );
          })}
          {hiddenLedgerCount > 0 ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.35, color: '#94a3b8', fontSize: 11 }}
            >
              {messages.pinLedgerHoverMore(hiddenLedgerCount)}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {actor ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: showActorFollows ? 0.5 : 0,
          }}
        >
          <Avatar
            src={actor.profileImageUrl || undefined}
            alt={actor.username}
            sx={{ width: 14, height: 14, fontSize: 8 }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              lineHeight: 1.2,
              color: '#e2e8f0',
            }}
          >
            @{actor.username}
          </Typography>
        </Box>
      ) : null}
      {showMeaningHeader ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: '#94a3b8',
            mb: 0.35,
            fontSize: 10,
          }}
        >
          AI meaning · {meaning.kind}
          {meaning.source ? ` · ${meaning.source}` : ''}
          {meaning.blockerDone
            ? ` · ${meaning.blockerDone.kind === 'partial' ? 'partial' : 'done'}:${meaning.blockerDone.doneKey}`
            : ''}
          {meaning.aiCost
            ? ` · ${formatAiCostLabel(meaning.aiCost)}${
              Array.isArray(meaning.aiCost.parts) && meaning.aiCost.parts.length
                ? ` (${meaning.aiCost.parts.map(p => p.label).filter(Boolean).join('+')})`
                : ''
            }`
            : ''}
        </Typography>
      ) : null}
      {blockerDoneLabel ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: (showPinHeader || showMeaningText) ? 0.5 : 0,
          }}
        >
          {isPartialFill ? (
            <Box
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: '#fcd34d',
                flexShrink: 0,
                lineHeight: 1,
                mt: '1px',
              }}
            >
              ~
            </Box>
          ) : (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 14, color: '#86efac', flexShrink: 0 }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              lineHeight: 1.3,
              color: isPartialFill ? '#fef3c7' : '#bbf7d0',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {blockerDoneLabel}
          </Typography>
        </Box>
      ) : null}
      {showPinHeader ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            mb: showMeaningText ? 0.5 : 0,
          }}
        >
          <PinHoverIcon icon={meaning.icon} />
          {meaning.title ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 600,
                lineHeight: 1.3,
                color: '#f1f5f9',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {meaning.title}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {showMeaningText ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.35,
            color: '#f8fafc',
            fontSize: 12,
          }}
        >
          {meaning.text}
        </Typography>
      ) : null}
      {showExtras.length > 0 ? (
        <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          {showExtras.map(extra => (
            <Typography
              key={`${extra.label}:${extra.text.slice(0, 24)}`}
              variant="caption"
              sx={{
                display: 'block',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.3,
                color: '#cbd5e1',
                fontSize: 11,
              }}
            >
              <Box component="span" sx={{ color: '#64748b', fontWeight: 600 }}>
                {extra.label}:{' '}
              </Box>
              {extra.text}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
